import React, { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { getCard, getSet } from "../lib/api";
import { cardPath, setPath } from "../lib/slug";
/**
 * Legacy redirect shims — owner directive 2026-02-01.
 *
 * Old URL shape                           → New canonical shape
 *   /sets                                 →  /pokemon/en/sets
 *   /sets/:externalId                     →  /pokemon/en/sets/{set-slug}
 *   /cards/:externalId                    →  /pokemon/en/sets/{set-slug}/{card-slug}/{number}
 *   /cards/:externalId/:variantSlug       →  /pokemon/en/sets/{set-slug}/{card-slug}/{number}[/{variant}]
 *
 * All redirects use `<Navigate replace />` (client-side). SPA
 * limitation note: React Router cannot emit true HTTP 301s from
 * static hosting. Crawlers see the replace navigation via
 * `<link rel="canonical">` on the destination pages (§ SetDetailPage,
 * CardDetailPage). PUBLISH_RUNBOOK.md documents recommended
 * server-side rewrite rules where the deploy platform supports them.
 */

export function LegacySetRedirect() {
  const { externalId } = useParams();
  const [state, setState] = useState({ status: "loading", to: null });

  useEffect(() => {
    let alive = true;
    getSet(externalId)
      .then((s) => {
        if (!alive) return;
        setState({ status: "ready", to: setPath("pokemon", "en", s.name || externalId) });
      })
      .catch(() => {
        if (!alive) return;
        // If the old external id doesn't resolve (or set is hidden),
        // send to the sets list — never 404 the user on a shared link.
        setState({ status: "ready", to: "/pokemon/en/sets" });
      });
    return () => {
      alive = false;
    };
  }, [externalId]);

  if (state.status !== "ready" || !state.to) {
    return (
      <div className="mx-auto w-full max-w-[1400px] px-5 py-24 text-body text-ink-muted"
           data-testid="legacy-set-redirect-loading">
        Redirecting…
      </div>
    );
  }
  return <Navigate to={state.to} replace />;
}

export function LegacyCardRedirect() {
  const { externalId, variantSlug } = useParams();
  const [state, setState] = useState({ status: "loading", to: null });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const card = await getCard(externalId);
        // The GET /cards/{ext} response nests the set under `card.set`
        // (external_id, name, ...). We can build the canonical URL
        // directly — no extra API call needed.
        const setName =
          card.set?.name || card.set?.external_id || externalId;
        const defaultVariantSlug = card.default_variant_slug || null;
        const qs = window.location.search.replace(/^\?/, "");
        const to =
          cardPath({
            game: "pokemon",
            lang: "en",
            setName,
            cardName: card.name || externalId,
            number: card.number,
            variantSlug: variantSlug || defaultVariantSlug,
            defaultVariantSlug,
          }) + (qs ? `?${qs}` : "");
        if (alive) setState({ status: "ready", to });
      } catch {
        if (alive) setState({ status: "ready", to: "/pokemon/en/sets" });
      }
    })();
    return () => {
      alive = false;
    };
    // `params` from useSearchParams returns a new URLSearchParams
    // instance every render — including it in deps caused an infinite
    // re-fetch. We snapshot the query string with window.location
    // inside the async closure instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalId, variantSlug]);

  if (state.status !== "ready" || !state.to) {
    return (
      <div className="mx-auto w-full max-w-[1400px] px-5 py-24 text-body text-ink-muted"
           data-testid="legacy-card-redirect-loading">
        Redirecting…
      </div>
    );
  }
  return <Navigate to={state.to} replace />;
}
